import React, { useEffect, useRef, useState } from "react";
import { Play, Pause, Headphones } from "lucide-react";

const AUDIO_URL =
    "https://customer-assets.emergentagent.com/job_ai-training-portal-6/artifacts/70lre296_AI_Rewires_the_Modern_University_Classroom.m4a";

const fmt = (s) => {
    if (!isFinite(s)) return "0:00";
    const m = Math.floor(s / 60);
    const sec = Math.floor(s % 60).toString().padStart(2, "0");
    return `${m}:${sec}`;
};

export default function PodcastSection() {
    const audioRef = useRef(null);
    const [playing, setPlaying] = useState(false);
    const [current, setCurrent] = useState(0);
    const [duration, setDuration] = useState(0);

    useEffect(() => {
        const a = audioRef.current;
        if (!a) return;
        const onTime = () => setCurrent(a.currentTime);
        const onMeta = () => setDuration(a.duration);
        const onEnd = () => setPlaying(false);
        a.addEventListener("timeupdate", onTime);
        a.addEventListener("loadedmetadata", onMeta);
        a.addEventListener("ended", onEnd);
        return () => {
            a.removeEventListener("timeupdate", onTime);
            a.removeEventListener("loadedmetadata", onMeta);
            a.removeEventListener("ended", onEnd);
        };
    }, []);

    const toggle = () => {
        const a = audioRef.current;
        if (!a) return;
        if (a.paused) {
            a.play();
            setPlaying(true);
        } else {
            a.pause();
            setPlaying(false);
        }
    };

    const seek = (e) => {
        const a = audioRef.current;
        if (!a || !duration) return;
        a.currentTime = Number(e.target.value);
        setCurrent(a.currentTime);
    };

    const pct = duration ? (current / duration) * 100 : 0;

    return (
        <section
            data-testid="podcast-section"
            className="border-b border-black/10 bg-black text-white"
        >
            <div className="container-edge py-20 lg:py-28">
                <div className="grid grid-cols-1 lg:grid-cols-12 gap-10">
                    <div className="lg:col-span-5">
                        <div className="inline-flex items-center gap-2 px-3 py-1 border border-white/20 text-[11px] tracking-[0.22em] uppercase font-bold">
                            <Headphones size={12} /> Listen first
                        </div>
                        <h2 className="mt-6 font-heading font-black text-4xl sm:text-5xl lg:text-6xl tracking-tighter leading-[0.95]">
                            AI Rewires the<br />Modern University<br />
                            <span className="text-klein bg-white px-2">Classroom.</span>
                        </h2>
                        <p className="mt-6 max-w-md text-white/70 leading-relaxed">
                            A short, honest podcast on what's actually changing in higher ed — grading, integrity,
                            lesson design — and what faculty can do about it this semester. Put it on in the car.
                        </p>
                    </div>

                    <div className="lg:col-span-7 flex">
                        <div className="w-full border border-white/15 p-8 lg:p-10 flex flex-col justify-between">
                            <div>
                                <p className="overline text-white/50">Episode 01 · Introduction</p>
                                <h3 className="mt-3 font-heading font-bold text-2xl lg:text-3xl tracking-tight leading-tight">
                                    AI Rewires the Modern University Classroom
                                </h3>
                                <p className="mt-3 text-sm text-white/60">
                                    Hosted by Gary Dobson · ai4faculty.info
                                </p>
                            </div>

                            <div className="mt-10 flex items-center gap-5">
                                <button
                                    type="button"
                                    onClick={toggle}
                                    data-testid="podcast-playpause"
                                    aria-label={playing ? "Pause podcast" : "Play podcast"}
                                    className="shrink-0 inline-flex items-center justify-center w-16 h-16 bg-klein text-white hover:bg-white hover:text-klein transition-colors"
                                >
                                    {playing ? <Pause size={24} /> : <Play size={24} className="translate-x-[1px]" />}
                                </button>

                                <div className="flex-1">
                                    <div className="relative h-2 bg-white/15">
                                        <div
                                            className="absolute top-0 left-0 h-full bg-klein"
                                            style={{ width: `${pct}%` }}
                                            data-testid="podcast-progress"
                                        />
                                        <input
                                            type="range"
                                            min={0}
                                            max={duration || 0}
                                            step={0.1}
                                            value={current}
                                            onChange={seek}
                                            aria-label="Seek"
                                            className="absolute inset-0 w-full h-full opacity-0 cursor-pointer"
                                        />
                                    </div>
                                    <div className="mt-2 flex items-center justify-between font-mono text-xs tracking-widest text-white/60">
                                        <span data-testid="podcast-current">{fmt(current)}</span>
                                        <span data-testid="podcast-duration">{fmt(duration)}</span>
                                    </div>
                                </div>
                            </div>

                            <audio ref={audioRef} src={AUDIO_URL} preload="metadata" data-testid="podcast-audio" />
                        </div>
                    </div>
                </div>
            </div>
        </section>
    );
}
